if (!customElements.get('ctfa-premade-review')) {
  customElements.define('ctfa-premade-review', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.closest('form');
      if (!this.form) return;
      this.messages = JSON.parse(this.querySelector('[data-review-messages]').textContent);
      this.fileMetadata ||= new Map();
      this.results = new Map();
      this.signature = '';
      this.revision ||= 0;
      this.mode ||= 'checkerboard';
      this.scope = this.form.closest('product-info') || this.form.parentElement;
      this.pendingFiles ||= new Set();
      this.pendingSelection ||= false;
      this.list = this.querySelector('[data-preview-list]');
      this.error = this.querySelector('[data-review-error]');
      this.confirm = this.querySelector('[data-artwork-confirm]');
      this.onComplete = (event) => {
        const detail = event.detail;
        if (!detail || String(detail.productId) !== this.dataset.productId || !this.fieldIds().includes(detail.fieldId)) return;
        const completed = (detail.files || []).filter((file) => file.uuid);
        if (!completed.length) return;
        this.pendingSelection = false;
        this.completedDuringBusy = this.uploadState().busy;
        this.pendingFiles = new Set(completed.map((file) => String(file.uuid)));
        completed.forEach((file) => this.fileMetadata.set(String(file.uuid), file));
        this.confirm.checked = false;
        this.updateSubmitState();
        this.schedule();
      };
      this.onSubmit = (event) => {
        if (!this.validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      this.onSubmitClick = (event) => {
        if (event.target.closest('[type="submit"]')) this.onSubmit(event);
      };
      this.onChange = (event) => {
        if (event.target.matches('[data-artwork-confirm]')) {
          this.error.hidden = true;
          this.updateSubmitState();
        }
      };
      this.onFileSelection = (event) => {
        const input = event.target;
        if (!input.matches('input[type="file"]') || !input.closest('#photouploadapp') || !input.files?.length) return;
        this.pendingSelection = true;
        this.pendingFiles.clear();
        this.confirm.checked = false;
        this.error.hidden = true;
        this.updateSubmitState();
      };
      this.onOrderChange = (event) => {
        const input = event.target;
        if (input.matches('[name="quantity"], [name="id"]') || input.closest('variant-selects')) this.reconcileOrder();
      };
      this.onMode = (event) => {
        const button = event.target.closest('[data-preview-mode]');
        if (!button || !this.contains(button) || !['light', 'dark', 'checkerboard'].includes(button.dataset.previewMode)) return;
        this.mode = button.dataset.previewMode;
        this.querySelectorAll('[data-preview-mode]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        this.querySelectorAll('.ctfa-premade-preview-stage').forEach((stage) => { stage.dataset.mode = this.mode; });
      };
      document.addEventListener('uploadly:upload-complete', this.onComplete);
      this.scope.addEventListener('change', this.onFileSelection, true);
      this.scope.addEventListener('change', this.onOrderChange, true);
      this.scope.addEventListener('input', this.onOrderChange, true);
      this.querySelectorAll('[data-preview-mode]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.previewMode === this.mode)));
      this.form.addEventListener('submit', this.onSubmit, true);
      this.form.addEventListener('click', this.onSubmitClick, true);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onMode);
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-cdn', 'data-img-id', 'value', 'name', 'id', 'data-ph-fieldname', 'data-status', 'aria-busy', 'aria-disabled', 'disabled', 'class'] });
      this.reconcile();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.timer);
      document.removeEventListener('uploadly:upload-complete', this.onComplete);
      this.scope?.removeEventListener('change', this.onFileSelection, true);
      this.scope?.removeEventListener('change', this.onOrderChange, true);
      this.scope?.removeEventListener('input', this.onOrderChange, true);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.form?.removeEventListener('click', this.onSubmitClick, true);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onMode);
      this.revision++;
      this.ready = false;
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.reconcile(), 50);
    }

    fieldIds() {
      const scope = this.form.closest('product-info') || this.form.parentElement;
      return Array.from(scope.querySelectorAll('#photouploadapp [id^="selBtn_"][data-ph-fieldname]')).filter((node) => node.getAttribute('data-ph-fieldname') === this.dataset.fieldName).map((node) => node.id.slice('selBtn_'.length));
    }

    uploadState() {
      const ids = this.fieldIds();
      const fields = Array.from(this.scope.querySelectorAll('#photouploadapp [id^="selBtn_"]')).filter((node) => ids.includes(node.id.slice('selBtn_'.length)));
      const widgets = fields.flatMap((field) => Array.from(field.querySelectorAll('.uploadcare--widget')));
      return {
        busy: widgets.some((widget) => widget.dataset.status === 'started' || widget.getAttribute('aria-busy') === 'true' || widget.querySelector('[aria-busy="true"]')),
        error: widgets.some((widget) => widget.dataset.status === 'error'),
        ready: widgets.length > 0 && widgets.every((widget) => widget.dataset.status === 'ready')
      };
    }

    isUploadProperty(name) {
      if (!name.startsWith('properties[') || !name.endsWith(']')) return false;
      const label = name.slice(11, -1);
      const fieldName = this.dataset.fieldName;
      return label === fieldName || (label.startsWith(`${fieldName} `) && /^\d+$/.test(label.slice(fieldName.length + 1)));
    }

    updateSubmitState() {
      const files = this.attachments();
      const states = Array.from(this.results.values());
      const upload = this.uploadState();
      const ready = files.length === 1 && this.confirm.checked && !upload.busy && !upload.error && !this.pendingSelection && !this.pendingFiles.size && states.length === files.length && !states.some((state) => state === 'loading' || state === 'unavailable');
      Array.from(this.form.elements).filter((element) => element.type === 'submit').forEach((button) => {
        if (!ready) {
          if (button.getAttribute('data-ctfa-review-blocked') !== 'true') button.setAttribute('data-ctfa-review-blocked', 'true');
          if (button.getAttribute('aria-disabled') !== 'true') {
            if (!button.hasAttribute('data-ctfa-review-aria-before')) button.setAttribute('data-ctfa-review-aria-before', button.getAttribute('aria-disabled') ?? '__absent__');
            button.setAttribute('aria-disabled', 'true');
          }
        } else {
          button.removeAttribute('data-ctfa-review-blocked');
          if (button.hasAttribute('data-ctfa-review-aria-before') && !button.disabled && !button.classList.contains('loading') && button.getAttribute('aria-busy') !== 'true') {
            const previous = button.getAttribute('data-ctfa-review-aria-before');
            if (button.getAttribute('aria-disabled') === 'true') {
              if (previous === '__absent__') button.removeAttribute('aria-disabled');
              else button.setAttribute('aria-disabled', previous);
            }
            button.removeAttribute('data-ctfa-review-aria-before');
          }
        }
      });
    }

    formatSize(value) {
      const match = String(value).match(/^\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(?:"|″|''|in(?:ches)?)?\s*$/i);
      return match ? `${match[1]} × ${match[2]}″` : null;
    }

    refreshSizes() {
      const inputs = Array.from(this.scope.querySelectorAll('variant-selects input[type="radio"]'));
      inputs.forEach((input) => {
        const size = this.formatSize(input.value);
        const fieldset = input.closest('fieldset');
        if (!size || !fieldset) return;
        const label = Array.from(fieldset.querySelectorAll('label[for]')).find((item) => item.htmlFor === input.id);
        const text = label && Array.from(label.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
        if (text && text.textContent.trim() !== size) text.textContent = size;
      });
      const selected = this.scope.querySelector('variant-selects input:checked, variant-selects select');
      const size = selected ? this.formatSize(selected.value) : null;
      this.scope.querySelectorAll('[data-ctfa-premade-selected-size]').forEach((helper) => {
        if (helper.textContent !== (size || '')) helper.textContent = size || '';
      });
    }

    reconcileOrder() {
      this.refreshSizes();
      const data = new FormData(this.form);
      const options = Array.from(this.scope.querySelectorAll('variant-selects input:checked, variant-selects select')).map((input) => [input.name, input.value]);
      const signature = JSON.stringify([data.getAll('id'), data.getAll('quantity'), options]);
      if (this.orderSignature !== undefined && signature !== this.orderSignature) {
        this.confirm.checked = false;
        this.error.textContent = this.messages.reset;
        this.error.hidden = false;
      }
      this.orderSignature = signature;
      this.updateSubmitState();
    }

    attachments() {
      const prefixes = this.fieldIds().map((id) => `imageFiles_${id}`);
      return Array.from(this.form.querySelectorAll('[data-img-id]')).filter((node) => prefixes.some((prefix) => node.id.startsWith(prefix))).map((node) => {
        const input = Array.from(node.querySelectorAll('input[name]')).find((item) => this.isUploadProperty(item.name));
        if (!input) return null;
        return { uuid: node.dataset.imgId, value: input.value, url: input.getAttribute('data-cdn') || '', name: this.fileMetadata.get(node.dataset.imgId)?.name || this.messages.fileLabel, mime: this.fileMetadata.get(node.dataset.imgId)?.mimeType || '' };
      }).filter(Boolean);
    }

    reconcile() {
      this.reconcileOrder();
      const upload = this.uploadState();
      if (upload.busy && !this.widgetWasBusy) {
        this.confirm.checked = false;
        if (!this.completedDuringBusy) this.pendingSelection = true;
      }
      if (upload.error) this.confirm.checked = false;
      if (upload.ready && !upload.busy && (this.widgetWasBusy || this.widgetHadError)) {
        this.pendingSelection = false;
        this.confirm.checked = false;
      }
      this.widgetHadError = upload.error;
      this.widgetWasBusy = upload.busy;
      if (!upload.busy) this.completedDuringBusy = false;
      const files = this.attachments();
      files.forEach((file) => { if (file.value && file.url) this.pendingFiles.delete(file.uuid); });
      const signature = JSON.stringify(files.map((file) => [file.uuid, file.value, file.url, file.mime, file.name]));
      if (signature === this.signature) { this.updateSubmitState(); return; }
      const changed = this.signature && this.signature !== '[]';
      this.signature = signature;
      this.revision++;
      this.results.clear();
      this.confirm.checked = false;
      this.list.replaceChildren();
      this.querySelector('[data-review-empty]').hidden = files.length > 0;
      this.querySelector('[data-preview-controls]').hidden = files.length === 0;
      this.querySelector('[data-preview-note]').hidden = files.length === 0;
      this.error.hidden = !changed;
      if (changed) this.error.textContent = this.messages.reset;
      files.forEach((file) => this.preview(file, this.revision));
      this.updateSubmitState();
    }

    async preview(file, revision) {
      const card = document.createElement('figure');
      card.className = 'ctfa-premade-preview-card';
      const stage = document.createElement('div');
      stage.className = 'ctfa-premade-preview-stage';
      stage.dataset.mode = this.mode;
      const caption = document.createElement('figcaption');
      caption.textContent = file.name;
      const status = document.createElement('p');
      status.className = 'ctfa-premade-file-status';
      status.textContent = this.messages.loading;
      card.append(stage, caption, status);
      this.list.append(card);
      this.results.set(file.uuid, 'loading');
      const finish = (result, message) => {
        if (revision !== this.revision || !this.isConnected) return;
        this.results.set(file.uuid, result);
        this.confirm.checked = false;
        status.dataset.result = result;
        status.textContent = message;
        this.updateSubmitState();
      };
      let url;
      if (!file.value || !file.url) return;
      try { url = new URL(file.url); } catch { finish('unavailable', this.messages.unsupported); stage.hidden = true; return; }
      if (url.protocol !== 'https:' || (file.mime && file.mime !== 'image/png')) {
        finish('unavailable', this.messages.unsupported); stage.hidden = true; return;
      }
      const image = new Image();
      image.alt = file.name;
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.src = url.href;
      let timeout;
      try {
        await Promise.race([image.decode(), new Promise((_, reject) => timeout = setTimeout(() => reject(new Error('Preview timed out')), 15000))]);
        if (revision !== this.revision || !this.isConnected) return;
        if (!image.naturalWidth || !image.naturalHeight) throw new Error('Empty preview');
        stage.append(image);
        const pixels = image.naturalWidth * image.naturalHeight;
        if (!pixels || pixels > 24000000 || !file.mime || /\/-\//.test(url.pathname)) {
          finish('unknown', this.messages.unknown); return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0);
        const data = context.getImageData(0, 0, canvas.width, canvas.height).data;
        let hasTransparency = false;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) { hasTransparency = true; break; }
        }
        canvas.width = 0;
        canvas.height = 0;
        finish(hasTransparency ? 'alpha' : 'opaque', hasTransparency ? this.messages.alpha : this.messages.opaque);
      } catch {
        if (stage.contains(image)) {
          finish('unknown', this.messages.unknown);
        } else {
          stage.hidden = true;
          finish('unavailable', this.messages.unavailableError);
        }
      } finally {
        clearTimeout(timeout);
      }
    }

    validate() {
      this.reconcile();
      let message = '';
      const states = Array.from(this.results.values());
      if (!this.attachments().length) message = this.messages.noUpload;
      else if (this.attachments().length !== 1) message = this.messages.unavailableError;
      else if (this.uploadState().error) message = this.messages.unavailableError;
      else if (this.uploadState().busy || this.pendingSelection || this.pendingFiles.size || states.includes('loading')) message = this.messages.loading;
      else if (states.includes('unavailable') || states.length !== this.attachments().length) message = this.messages.unavailableError;
      else if (!this.confirm.checked) message = this.messages.confirmError;
      if (message) {
        this.error.textContent = message;
        this.error.hidden = false;
        this.error.focus({ preventScroll: true });
        this.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      this.error.hidden = true;
      return true;
    }
  });
}

